import { createContext, useContext, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { CONTACT } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EstimateCtx = createContext({ open: () => {} });
export const useEstimate = () => useContext(EstimateCtx);

const empty = { name: "", email: "", phone: "", message: "" };

export const EstimateProvider = ({ children }) => {
  const [isOpen, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const open = () => { setDone(false); setForm(empty); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      toast.success("Estimate request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <EstimateCtx.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="estimate-modal"
          >
            <div className="absolute inset-0 bg-ffc-ink/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="relative w-full max-w-lg rounded-2xl bg-white p-7 sm:p-9 shadow-2xl"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute right-5 top-5 text-ffc-gray hover:text-ffc-red transition-colors"
                data-testid="estimate-close-btn" aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {done ? (
                <div className="py-8 text-center" data-testid="estimate-success">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-ffc-blue" />
                  <h3 className="font-display mt-5 text-3xl">Request received.</h3>
                  <p className="mt-3 text-ffc-gray">
                    Thanks, {form.name.split(" ")[0] || "there"}. A Fast Fix Charlotte specialist
                    will reach out shortly with your free estimate.
                  </p>
                  <a href={CONTACT.phoneHref} className="mt-6 inline-flex items-center gap-2 font-semibold text-ffc-blue">
                    <Phone className="h-4 w-4" /> {CONTACT.phone}
                  </a>
                </div>
              ) : (
                <>
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-ffc-red">Free Estimate</span>
                  <h3 className="font-display mt-2 text-3xl sm:text-4xl">Tell us about your project.</h3>
                  <p className="mt-2 text-sm text-ffc-gray">No pressure, no obligation. Response within one business day.</p>

                  <form onSubmit={submit} className="mt-6 space-y-4" data-testid="estimate-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="e-name">Full name</Label>
                        <Input id="e-name" data-testid="estimate-name" value={form.name}
                          onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="mt-1.5" />
                      </div>
                      <div>
                        <Label htmlFor="e-phone">Phone</Label>
                        <Input id="e-phone" data-testid="estimate-phone" value={form.phone}
                          onChange={(e) => set("phone", e.target.value)} placeholder="(704) 201-4261" className="mt-1.5" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="e-email">Email</Label>
                      <Input id="e-email" type="email" data-testid="estimate-email" value={form.email}
                        onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="e-msg">Project details</Label>
                      <Textarea id="e-msg" data-testid="estimate-message" value={form.message}
                        onChange={(e) => set("message", e.target.value)} rows={4}
                        placeholder="Tell us what you'd like done..." className="mt-1.5" />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="estimate-submit"
                      className="btn-red h-12 w-full rounded-full text-base font-semibold hover:bg-ffc-red">
                      {loading ? "Sending..." : <>Request Free Estimate <Send className="ml-2 h-4 w-4" /></>}
                    </Button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </EstimateCtx.Provider>
  );
};
